import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { request, tokenStore } from '../api/client'

/** Admin roles. Server enforces; the UI only hides — see PHASE-019. */
export const ROLES = [
  'SUPER_ADMIN',
  'OPS',
  'KYC_REVIEWER',
  'FINANCE',
  'SUPPORT',
  'MARKETING',
  'STATE',
] as const
export type Role = (typeof ROLES)[number]

export type CurrentUser = {
  id: string
  firstName: string
  lastName: string
  email: string
  roles: Role[]
  avatarUrl?: string
}

type AuthState =
  { status: 'loading' } | { status: 'authenticated'; user: CurrentUser } | { status: 'anonymous' }

/** What the server answers step one with. Exactly one side is populated. */
type LoginResponse = {
  accessToken?: string
  expiresIn?: number
  challenge?: 'EMAIL' | 'SMS'
  destination?: string
}

/** Null when the password was enough; otherwise where the code was sent. */
export type LoginChallenge = { channel: 'EMAIL' | 'SMS'; destination: string } | null

type AuthContextValue = {
  state: AuthState
  login: (email: string, password: string) => Promise<LoginChallenge>
  verifyLogin: (email: string, password: string, code: string) => Promise<void>
  resendLoginCode: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hasRole: (...roles: Role[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  // On boot, try the refresh cookie. A page reload must not log the user out.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { accessToken } = await request<{ accessToken: string }>('/auth/refresh', {
          method: 'POST',
          auth: false,
        })
        tokenStore.set(accessToken)
        const user = await request<CurrentUser>('/auth/me')
        if (!cancelled) setState({ status: 'authenticated', user })
      } catch {
        if (!cancelled) setState({ status: 'anonymous' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /** Takes a fresh access token and loads whoever it belongs to. */
  const adopt = useCallback(async (accessToken: string) => {
    tokenStore.set(accessToken)
    const user = await request<CurrentUser>('/auth/me')
    setState({ status: 'authenticated', user })
  }, [])

  /**
   * Step one. A staff account gets a challenge rather than a session — the
   * password alone is never enough for an account that can suspend other
   * accounts and settle payments (BO frames `4081:467`, `4081:537`).
   */
  const login = useCallback(
    async (email: string, password: string): Promise<LoginChallenge> => {
      const response = await request<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
        auth: false,
      })
      if (response.accessToken) {
        await adopt(response.accessToken)
        return null
      }
      return { channel: response.challenge ?? 'EMAIL', destination: response.destination ?? '' }
    },
    [adopt],
  )

  /** Step two: the code from that challenge. */
  const verifyLogin = useCallback(
    async (email: string, password: string, code: string) => {
      const { accessToken } = await request<LoginResponse>('/auth/login/verify', {
        method: 'POST',
        body: { email, password, code },
        auth: false,
      })
      await adopt(accessToken ?? '')
    },
    [adopt],
  )

  const resendLoginCode = useCallback(async (email: string, password: string) => {
    await request<LoginResponse>('/auth/login/resend', {
      method: 'POST',
      body: { email, password },
      auth: false,
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      await request<void>('/auth/logout', { method: 'POST' })
    } finally {
      tokenStore.set(null)
      setState({ status: 'anonymous' })
    }
  }, [])

  const hasRole = useCallback(
    (...roles: Role[]) =>
      state.status === 'authenticated' &&
      (state.user.roles.includes('SUPER_ADMIN') || roles.some((r) => state.user.roles.includes(r))),
    [state],
  )

  const value = useMemo(
    () => ({ state, login, verifyLogin, resendLoginCode, logout, hasRole }),
    [state, login, verifyLogin, resendLoginCode, logout, hasRole],
  )
  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
